import React from "react";

const SKY = {
  morning: "linear-gradient(#cfeaf5,#f3e6c4)",
  afternoon: "linear-gradient(#BFE3F2,#E8D9A8)",
  evening: "linear-gradient(#f7b26a,#e59f6a)",
  night: "linear-gradient(#161f3e,#2b2f52)",
};
const ROAD = {
  morning: "linear-gradient(#98a2b3,#727a86)",
  afternoon: "linear-gradient(#8D99AE,#6B7280)",
  evening: "linear-gradient(#7c7f92,#565a68)",
  night: "linear-gradient(#3a3e52,#2a2d3c)",
};
const PATH = { morning: "#cbb07a", afternoon: "#C9A66B", evening: "#b98e52", night: "#5a5238" };

const Sign = ({ text, c = "#FDF6E3", fg = "#3a2617", rot = 0, cls = "" }) => (
  <div className={`rounded-[2px] border-2 px-1 py-[1px] ${cls}`} style={{ background: c, borderColor: fg, transform: `rotate(${rot}deg)` }}>
    <span className="font-signboard block text-[6px] font-bold leading-tight sm:text-[7px]" style={{ color: fg }}>{text}</span>
  </div>
);

// A neighbour building with random 90s details.
const Building = ({ left, w = 20, h = 22, wall, roof = "#9c3b2e", details = {} }) => (
  <div className="absolute" style={{ left: `${left}%`, top: `${8}%` }}>
    <div className="relative rounded-sm border-2 border-[#5c3d22]" style={{ width: `${w}vw`, maxWidth: w * 5, height: `${h}vh`, maxHeight: h * 4, background: wall }}>
      {/* roof */}
      <div className="absolute -top-2 left-1/2 h-3 w-[108%] -translate-x-1/2 rounded-t" style={{ background: roof }} />
      {/* cracked plaster patch */}
      <div className="absolute right-1 top-1/3 h-6 w-5 rotate-3 rounded bg-black/10" />
      <div className="absolute left-2 top-2 h-4 w-6 -rotate-2 rounded" style={{ background: "rgba(255,255,255,0.18)" }} />
      {/* window with grill + curtain */}
      <div className="absolute left-2 top-4 h-6 w-6 rounded-sm border-2 border-[#5c3d22] bg-[#3d5a6c]">
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
          <span className="border border-[#5c3d22]/50" /><span className="border border-[#5c3d22]/50" />
          <span className="border border-[#5c3d22]/50" /><span className="border border-[#5c3d22]/50" />
        </div>
        <div className="absolute right-0 top-0 h-full w-2 bg-[#E76F51]/70" />
      </div>
      {/* balcony + hanging clothes */}
      {details.balcony && (
        <div className="absolute -right-1 top-8 h-6 w-10 rounded-b border-2 border-[#5c3d22] bg-[#b9975b]">
          <div className="absolute -top-3 left-0 flex w-full justify-around">
            {["#457B9D", "#E76F51", "#2D6A4F", "#E9C46A"].map((c, i) => (
              <span key={i} className="hang-cloth h-4 w-1.5 rounded-b" style={{ background: c, animationDelay: `${i * 0.3}s` }} />
            ))}
          </div>
          <div className="absolute inset-x-1 top-1 grid grid-cols-3 gap-[3px]">
            <span className="h-3 border border-[#5c3d22]/60" /><span className="h-3 border border-[#5c3d22]/60" /><span className="h-3 border border-[#5c3d22]/60" />
          </div>
        </div>
      )}
      {/* water tank */}
      {details.tank && <div className="absolute -top-6 right-1 h-4 w-5 rounded-t-md border-2 border-[#3a2617] bg-[#1f6f9c]" />}
      {/* satellite dish */}
      {details.dish && <div className="absolute -top-3 left-3 h-3 w-4 rounded-full border-2 border-[#888] bg-white/70" />}
      {/* window AC + exhaust */}
      {details.ac && <div className="absolute right-1 top-12 h-3 w-5 rounded-sm border border-[#5c3d22] bg-[#dfe3e8]" />}
      {/* flower pot */}
      {details.pot && <div className="absolute -bottom-0 left-1 h-3 w-2 rounded-b bg-[#b5651d]"><span className="absolute -top-1.5 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-[#2D6A4F]" /></div>}
      {/* awning */}
      {details.awning && <div className="absolute -bottom-1 left-1/2 h-2 w-[70%] -translate-x-1/2 bg-[repeating-linear-gradient(90deg,#C84B31_0_6px,#FDF6E3_6px_12px)]" />}
      {/* nameplate + doorbell */}
      <div className="absolute bottom-1 left-2 flex items-center gap-1">
        <div className="rounded-[1px] border border-[#5c3d22] bg-[#d9c19a] px-1 text-[5px] font-bold text-[#3a2617]">{details.name || "SHARMA"}</div>
        <span className="h-1.5 w-1.5 rounded-full bg-kirana-red" />
      </div>
      {/* iron grill door */}
      <div className="absolute bottom-0 right-3 h-8 w-5 border-2 border-[#5c3d22] bg-[#6E5238]">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent_0_2px,rgba(0,0,0,0.25)_2px_3px)]" />
      </div>
    </div>
  </div>
);

// Old scooter / small car / auto / thela (side view, CSS).
const Chetak = ({ style }) => (
  <div className="absolute" style={style}>
    <div className="relative h-6 w-14">
      <div className="absolute bottom-2 left-1 h-4 w-10 rounded-t-xl border-2 border-[#3a2617] bg-[#3f7a9c]" />
      <div className="absolute bottom-3 right-0 h-3 w-3 rounded-full border-2 border-[#3a2617] bg-[#c9c9c9]" />
      <span className="absolute bottom-0 left-1 h-3 w-3 rounded-full border-2 border-[#3a2617] bg-[#2b2b2b]" />
      <span className="absolute bottom-0 right-1 h-3 w-3 rounded-full border-2 border-[#3a2617] bg-[#2b2b2b]" />
    </div>
  </div>
);
const Maruti = ({ style }) => (
  <div className="absolute" style={style}>
    <div className="relative h-7 w-20">
      <div className="absolute bottom-2 h-3 w-20 rounded-md border-2 border-[#3a2617] bg-[#e8e4dc]" />
      <div className="absolute bottom-4 left-3 h-3 w-12 rounded-t-lg border-2 border-[#3a2617] bg-[#e8e4dc]" />
      <div className="absolute bottom-5 left-4 h-2 w-9 bg-[#8fb3c9]" />
      <span className="absolute bottom-0 left-3 h-4 w-4 rounded-full border-[3px] border-[#2b2b2b] bg-[#555]" />
      <span className="absolute bottom-0 right-3 h-4 w-4 rounded-full border-[3px] border-[#2b2b2b] bg-[#555]" />
      <span className="absolute bottom-2 right-0 h-1 w-1 rounded-full bg-kirana-red" />
      <div className="absolute bottom-[6px] right-2 rounded-[1px] bg-[#f5d24a] px-[2px] text-[4px] font-bold text-black">DL 3C</div>
    </div>
  </div>
);
const Auto = ({ style }) => (
  <div className="absolute" style={style}>
    <div className="relative h-8 w-16">
      <div className="absolute bottom-3 left-3 h-5 w-11 rounded-t-2xl border-2 border-[#3a2617] bg-[#f7d64a]" />
      <div className="absolute bottom-3 left-3 h-3 w-11 rounded-t bg-[#1c1c1c]" />
      <div className="absolute bottom-3 left-0 h-3 w-4 rounded-tl-xl border-2 border-[#3a2617] bg-[#f7d64a]" />
      <span className="absolute bottom-0 left-1 h-3 w-3 rounded-full border-2 border-[#3a2617] bg-[#2b2b2b]" />
      <span className="absolute bottom-0 right-3 h-4 w-4 rounded-full border-2 border-[#3a2617] bg-[#2b2b2b]" />
    </div>
  </div>
);
const Thela = ({ style }) => (
  <div className="absolute" style={style}>
    <div className="relative h-9 w-16">
      <div className="absolute bottom-3 h-3 w-16 rounded-sm border-2 border-[#5c3d22] bg-[#a9773f]" />
      <div className="absolute bottom-6 left-1 flex gap-[3px]">
        {["#E63946", "#2D6A4F", "#E76F51", "#E9C46A", "#8AB17D"].map((c, i) => (
          <span key={i} className="h-2 w-2 rounded-full" style={{ background: c }} />
        ))}
      </div>
      <span className="absolute bottom-0 left-3 h-4 w-4 rounded-full border-2 border-[#3a2617] bg-[#2b2b2b]" />
    </div>
  </div>
);

// Non-interactive decorative backdrop for the outside 1990s gali.
export const StreetDecor = ({ timeOfDay = "afternoon" }) => {
  const lit = timeOfDay === "evening" || timeOfDay === "night";
  const night = timeOfDay === "night";
  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      {/* sky / path / road */}
      <div className="absolute inset-x-0 top-0 h-[38%]" style={{ background: SKY[timeOfDay] }} />
      {night && <div className="absolute right-[12%] top-[5%] h-6 w-6 rounded-full bg-[#f5f3d0] shadow-[0_0_18px_6px_rgba(245,243,208,0.5)]" />}
      <div className="absolute inset-x-0 top-[38%] h-[24%]" style={{ background: PATH[timeOfDay] }} />
      <div className="absolute inset-x-0 bottom-0 top-[62%]" style={{ background: ROAD[timeOfDay] }} />
      <div className="absolute inset-x-0 top-[62%] h-1.5 bg-kirana-brown/40" />

      {/* road texture: cracks, patches, puddles, manhole, drain, stones, faded chalk */}
      <div className="absolute left-[10%] top-[72%] h-[1px] w-[22%] rotate-2 bg-black/25" />
      <div className="absolute left-[30%] top-[84%] h-[1px] w-[18%] -rotate-3 bg-black/25" />
      <div className="absolute left-[60%] top-[78%] h-8 w-16 rounded-[40%] bg-black/15" />
      <div className="absolute left-[24%] top-[88%] h-5 w-12 rounded-[50%] bg-[#4a6a7a]/50" />
      <div className="absolute right-[16%] top-[82%] h-6 w-6 rounded-full border-2 border-black/40 bg-[#4b4b4b]" />
      <div className="absolute left-[6%] top-[92%] h-2 w-[30%] rounded bg-black/20" />
      {[["12%", "80%"], ["44%", "90%"], ["70%", "86%"], ["82%", "76%"], ["36%", "76%"]].map(([l, t], i) => (
        <span key={`stone-${i}`} className="absolute h-1 w-1.5 rounded-full bg-[#555]" style={{ left: l, top: t }} />
      ))}
      <div className="absolute left-[52%] top-[70%] font-signboard text-[10px] text-white/30">▨ ▨ ▨</div>
      {/* weeds near walls */}
      {["8%", "40%", "76%"].map((l, i) => (
        <span key={`weed-${i}`} className="absolute bottom-[38%] h-3 w-2 rounded-t-full bg-[#3f6b3a]/70" style={{ left: l }} />
      ))}
      {/* litter */}
      <div className="absolute left-[58%] top-[88%] h-2 w-3 rotate-12 bg-[#f2f2e6] shadow-sm" />
      <div className="absolute left-[20%] top-[92%] h-3 w-4 -rotate-6 rounded-sm bg-[#d9e2e8]/70" />
      <div className="absolute right-[30%] top-[90%] h-2 w-2 rounded-full bg-[#c94] " />
      <div className="absolute right-[8%] top-[92%] h-3 w-5 rotate-3 rounded-[2px] border border-[#7a5a3a] bg-[#c79a68]" />

      {/* neighbour buildings (varied, lived-in) */}
      <Building left={2} w={17} h={24} wall="#E9C46A" roof="#9c3b2e" details={{ balcony: true, tank: true, pot: true, name: "VERMA" }} />
      <Building left={20} w={15} h={20} wall="#cfe3d0" roof="#3d6a4f" details={{ dish: true, ac: true, name: "GUPTA", awning: true }} />
      <Building left={68} w={15} h={21} wall="#f2c9d4" roof="#7c3f8c" details={{ balcony: true, dish: true, name: "KHAN" }} />
      <Building left={85} w={14} h={19} wall="#bcd3e6" roof="#c0642c" details={{ tank: true, ac: true, pot: true, name: "RAO" }} />

      {/* kirana shop facade (center, behind entrance zone) */}
      <div className="absolute left-1/2 top-[15%] h-24 w-52 -translate-x-1/2 rounded-sm border-[3px] border-kirana-brown bg-[#B98E52]">
        <div className="mx-auto mt-1 w-44 rounded-sm border-2 border-kirana-brown bg-kirana-red py-1 text-center font-signboard text-sm font-bold text-white">Sharma Kirana Store</div>
        <div className="mx-auto mt-1 h-3 w-40 bg-[repeating-linear-gradient(90deg,#C84B31_0_12px,#FDF6E3_12px_24px)]" />
        <div className="mx-auto mt-1 flex w-40 justify-between px-1">
          <Sign text="AAJ CASH KAL UDHAAR" c="#2F4F3A" fg="#FDF6E3" rot={-2} />
          <Sign text="GENERAL STORE" c="#E9C46A" rot={2} />
        </div>
        {lit && <div className="absolute inset-0 rounded-sm bg-[#ffdf9e]/20 shadow-[0_0_30px_10px_rgba(255,210,120,0.35)]" />}
      </div>

      {/* electric poles + overhead wire network + junction box + street lamp + kite */}
      <div className="absolute left-[37%] top-[6%] h-24 w-1.5 bg-[#4a3a2a]" />
      <div className="absolute right-[8%] top-[8%] h-20 w-1.5 bg-[#4a3a2a]" />
      {[9, 12, 15, 18].map((t, i) => (
        <div key={`wire-${i}`} className="absolute left-[37%] h-[1.5px] w-[55%] bg-black/40" style={{ top: `${t}%`, transform: `rotate(${i % 2 ? 1.5 : -1}deg)` }} />
      ))}
      <div className="absolute left-[36%] top-[8%] h-4 w-3 rounded-sm border border-black/50 bg-[#5a5a5a]" />
      {/* kite stuck in wires */}
      <div className="wire-kite absolute left-[62%] top-[11%] h-4 w-4 rotate-45 bg-[#E63946] shadow"><span className="absolute inset-0 border border-white/60" /></div>
      <div className="absolute left-[63%] top-[14%] h-6 w-[1px] rotate-12 bg-black/30" />
      {/* street lamp */}
      <div className="absolute left-[9%] top-[30%] h-16 w-1.5 bg-[#4a3a2a]" />
      <div className={`absolute left-[7.5%] top-[29%] h-3 w-5 rounded-full ${lit ? "lamp-flicker bg-[#ffe08a] shadow-[0_0_20px_8px_rgba(255,220,130,0.6)]" : "bg-[#c9c2a8]"}`} />

      {/* faded / torn wall posters + hand-painted signs */}
      <div className="absolute left-[22%] top-[40%] flex -rotate-1 flex-col gap-[3px]">
        <Sign text="STD ISD PCO" c="#f7d64a" rot={-1} />
        <Sign text="XEROX AVAILABLE" c="#FDF6E3" rot={1} />
        <Sign text="COMPUTER CLASSES" c="#cfe3d0" rot={-2} />
      </div>
      <div className="absolute right-[22%] top-[41%] flex rotate-1 flex-col gap-[3px]">
        <Sign text="TUITION CLASSES" c="#f2c9d4" rot={1} />
        <Sign text="TO LET" c="#FDF6E3" rot={-3} />
        <Sign text="CABLE CONNECTION" c="#bcd3e6" rot={2} />
      </div>
      <div className="absolute left-[40%] top-[57%]"><Sign text="KRIPYA DEEWAR GANDI NA KARE" c="#FDF6E3" fg="#8a1c1c" rot={-1} /></div>
      <div className="absolute right-[6%] top-[54%]"><Sign text="NO PARKING" c="#FDF6E3" fg="#8a1c1c" rot={2} /></div>
      <div className="absolute left-[6%] top-[56%]"><Sign text="HORN OK PLEASE" c="#2F4F3A" fg="#FDF6E3" rot={-2} /></div>
      {/* torn old election-style poster */}
      <div className="absolute left-[52%] top-[42%] h-9 w-10 rotate-2 overflow-hidden rounded-[2px] border border-[#5c3d22] bg-[#E9C46A]">
        <div className="h-2 bg-[#2D6A4F]" /><div className="p-[2px] text-[5px] font-bold text-[#5c3d22]">VOTE ✋</div>
        <div className="absolute bottom-0 right-0 h-4 w-4 rotate-45 bg-[#B98E52]" />
      </div>
      {/* paan stains + faded chalk kid drawing */}
      <div className="absolute left-[16%] top-[58%] h-3 w-2 rounded-b-full bg-[#8a1c1c]/50" />
      <div className="absolute left-[17%] top-[60%] h-2 w-2 rounded-full bg-[#8a1c1c]/40" />
      <div className="absolute right-[36%] top-[68%] font-signboard text-[8px] text-white/40">☀ ♡ ⌂</div>
      {/* micro-discovery: RAHUL loves PRIYA */}
      <div className="absolute left-[70%] top-[52%] -rotate-2 font-signboard text-[7px] font-bold text-[#8a1c1c]/70">RAHUL ❤ PRIYA</div>

      {/* PCO / STD booth (interactive zone sits on top of this, left side) */}
      <div className="absolute left-[16%] top-[52%] -translate-x-1/2">
        <div className="relative h-16 w-12 rounded-sm border-[3px] border-[#3a2617] bg-[#f7d64a]">
          <div className="absolute -top-4 left-1/2 w-14 -translate-x-1/2 rounded-sm border-2 border-[#3a2617] bg-[#c1121f] py-[1px] text-center font-signboard text-[7px] font-bold text-white">STD ISD PCO</div>
          <div className="absolute inset-x-1 top-2 h-7 rounded-sm border-2 border-[#3a2617] bg-[#9fd3e6]/70" />
          <div className="absolute inset-x-2 bottom-1 h-4 rounded-sm border border-[#3a2617] bg-[#b98e52]" />
          <span className="absolute right-2 top-3 h-3 w-2 rounded-sm bg-[#e8e4dc]" />
        </div>
      </div>

      {/* parked / moving vehicles */}
      <Chetak style={{ bottom: "10%", left: "60%", transform: "rotate(-2deg)" }} />
      <Maruti style={{ bottom: "7%", left: "5%" }} />
      <Auto style={{ bottom: "9%", right: "4%", transform: "rotate(1deg)" }} />
      <Thela style={{ bottom: "18%", left: "40%" }} />

      {/* cricket stumps (bricks) + chalk boundary near the kids */}
      <div className="absolute bottom-[9%] right-[26%] flex gap-0.5">
        <div className="h-8 w-1 bg-kirana-cream" /><div className="h-8 w-1 bg-kirana-cream" /><div className="h-8 w-1 bg-kirana-cream" />
      </div>
      <div className="absolute bottom-[8%] right-[20%] h-[1px] w-16 bg-white/50" />

      {/* evening/night colour wash + vignette */}
      {timeOfDay === "evening" && <div className="absolute inset-0 bg-[#ff8c3a]/10 mix-blend-multiply" />}
      {night && <div className="absolute inset-0 bg-[#0b1030]/45" />}
    </div>
  );
};
